/* eslint-disable max-len */
import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Button } from '@michelin/acid-components';

interface HelpModalProps {
  open: boolean,
  onClose: any,
  type: 'level1' | 'level2' | 'level3' | 'level4' | 'level5' | 'tire',
}

export default function HelpModal(props: HelpModalProps) {
  const { type, open } = props;
  const { t } = useTranslation();

  const title = () => {
    switch (type) {
      case 'level1': return t('Level I - Basic Mechanical - Trailer');
      case 'level2': return t('Level II - Light Mechanical - Power Unit & Trailer');
      case 'level3': return t('Level III - Wet Mechanical Service');
      case 'level4': return t('Level IV - Full Mechanical');
      case 'tire': return t('Tire Services');
      default: return '';
    }
  };

  const headerHelper = (headerType: string) => {
    switch (headerType) {
      case 'duty': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('Light/Medium/Heavy Duty')}</Typography>
          <ul>
            <li>
              {t('Light Duty')}
              <ul>
                <li>{t('Class 1: Truck GVWR from 0 to 6,000 pounds (0 to 2,722 kg).')}</li>
                <li>{t('Class 2: Truck GVWR from 6,001 to 10,000 pounds (2,722 to 4,536 kg). Class 2 is subdivided into Class 2A and Class 2B, with Class 2A being 6,001 to 8,500 pounds (2,722 to 3,856 kg) pounds, and Class 2B being 8,501 to 10,000 pounds (3,856 to 4,536 kg) pounds')}</li>
                <li>{t('Class 3: Truck GVWR from 10,001 to 14,000 pounds (4,536 to 6,350 kg)')}</li>
              </ul>
            </li>
            <li>
              {t('Medium Duty')}
              <ul>
                <li>{t('Class 4: Truck GVWR from 14,001 to 16,000 pounds (6,351 to 7,257 kg).')}</li>
                <li>{t('Class 5: Truck GVWR from 16,001 to 19,500 pounds (7,258 to 8,845 kg).')}</li>
                <li>{t('Class 6: Truck GVWR from 19,501 to 26,000 pounds (8,846 to 11,793 kg).')}</li>
              </ul>
            </li>
            <li>
              {t('Heavy Duty')}
              <ul>
                <li>{t('Class 7: Truck GVWR ranges from 26,001 to 33,000 pounds (11,794 to 14,969 kg).')}</li>
                <li>{t('Class 8: Truck GVWR includes anything above 33,000 pounds (14,969 kg). These include all tractor trailer trucks.')}</li>
              </ul>
            </li>
          </ul>
        </>
      );

      case 'basic/advanced': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('Services definitions')}</Typography>
          <ul>
            <li>{`${t('Electrical Diagnostics')}: ${t('All electrical work not covered in previous levels, including rewiring, fault and short tracing with OEM wiring diagrams, installation of new electrical components, etc.')}`}</li>
            <li>
              {t('Engine Diagnostics')}
              <ul>
                <li>{`${t('Basic')}: ${t('Includes all work not covered in previous levels, excepting rebuilds and replacements.  Covers internal engine repair, advanced computer diagnostic capability, Regens, etc.')}`}</li>
                <li>{`${t('Advanced')}: ${t('Engine Replacement Capability, Engine Rebuild capability')}`}</li>
              </ul>
            </li>
            <li>
              {t('Transmission Diagnostics')}
              <ul>
                <li>{`${t('Basic')}: ${t('Transmission Diagnosis and Repair, up to but not including rebuild and replace.')}`}</li>
                <li>{`${t('Advanced')}: ${t('Transmission Rebuilds and Replacements.')}`}</li>
              </ul>
            </li>
          </ul>
        </>
      );
      default: return null;
    }
  };

  const content = () => {
    switch (type) {
      case 'tire': return (headerHelper('duty'));
      case 'level1': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('General')}</Typography>
          <ul>
            <li>{t('Certified to Perform DOT Inspections and Brake Inspection on Trailers')}</li>
            <li>{t('Perform PM – A Services on Trailers')}</li>
            <li>
              {t('Perform Basic Services on Trailers')}
              <ul>
                <li>{t('Trailer Body Repairs and Door Replacements (depending on complexity)')}</li>
                <li>{t('Suspension Work')}</li>
                <li>{t('Electrical work, including lights')}</li>
                <li>{t('Air and hydraulic brakes')}</li>
                <li>{t('Wheel Ends')}</li>
              </ul>
            </li>
            <li>{t('Conspicuity Marking Replacements on Trailers')}</li>
          </ul>
        </>
      );
      case 'level2': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('General')}</Typography>
          <ul>
            <li>{t('All of Level I')}</li>
            <li>{t('Addition of same services on Power Units')}</li>
            <li>{t('Perform Front End Work')}</li>
            <li>{t('Welding')}</li>
            <li>{t('Perform Alignment Services on Power Units and Trailers(In Bay or Mobile)')}</li>
          </ul>
          {headerHelper('duty')}
        </>
      );
      case 'level3': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('General')}</Typography>
          <ul>
            <li>{t('All of Level II')}</li>
            <li>{t('Intermediate Electrical Diagnosis and Repair')}</li>
            <li>{t('Addition of PM – B Services')}</li>
          </ul>
          {headerHelper('duty')}
        </>
      );
      case 'level4': return (
        <>
          <Typography style={{ fontWeight: 'bold' }}>{t('General')}</Typography>
          <ul>
            <li>{t('All of Level III')}</li>
            <li>{t('Able to Service all components on both Power Unit and Trailers')}</li>
          </ul>
          {headerHelper('duty')}
          {headerHelper('basic/advanced')}
        </>
      );
      default: return '';
    }
  };

  return (
    <Dialog
      onClose={() => props.onClose?.()}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle>
        {title()}
      </DialogTitle>

      <DialogContent dividers>
        {content()}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          color="primary"
          style={{ margin: '8px' }}
          onClick={() => props.onClose?.()}
        >
          {t('Done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
